// The daily window — what "Daily Total" counts.
//
// ── THIS IS A PORT. The canonical definition is in the toolbox ──────────────
//   certags-toolbox/src/lib/dailyWindow.js
// which holds the same rule in JS and in SQL, and is proved self-consistent by
// certags-toolbox/scripts/intel/tests/dailyWindow.test.mjs. The two repos share
// one Supabase database but no code, so this is a copy, in the same way
// commissionSnapshot.js is. If you change the rule, change it there first and
// re-run that test — the SQL half feeds dashboard_month_rollup(), which the
// toolbox Dashboard reads, and the two figures sit on screens beside each
// other both labelled "Daily Total".
//
// ── Why the rule exists ─────────────────────────────────────────────────────
// RULED 2026-10-12 (CRC): no daily email goes out Saturday or Sunday, so
// weekend entries fell into a gap and appeared in NO daily total, ever. DDJ
// logged three orders at 19:27–19:57 Pacific on Sunday 2026-10-11 totalling
// $34,997.50; the Monday 7am Daily Total read $0.00. Across all history, 26
// Saturday entries ($30,704) and 5 Sunday ($36,587) had never been counted.
//
// The window ENDS at the day being reported and STARTS at Pacific midnight of:
//
//     Monday    -> the preceding Saturday   (-2 days)
//     Sunday    -> the preceding Saturday   (-1 day)
//     Tue–Sat   -> that same day            ( 0 days)
//
// So the figure only grows until an email goes out, and Tuesday starts clean.
//
// ── What this does NOT change ───────────────────────────────────────────────
//   • unsentEntries / the subject line, which stay on the exact effective day
//   • elapsedBiz, dailyAvg and pace, which are business-day math
//
// ── THE BASIS IS NOW SETTLED, AND IT IS THIS REPO'S ─────────────────────────
// RULED 2026-10-12, second ruling: unify on date_str. 103 made the window
// identical on both sides and left the column split — this repo on date_str (a
// STORED business date), the toolbox RPC on created_at (arrival). Those are
// different questions: 159 of 2090 live entries carry a date_str 1–31 days
// EARLIER than created_at's Pacific date, all deliberate backfills, so
// backfilling a March order today landed it in the RPC's daily total and not in
// this one.
//
// Toolbox migration 104 moved the RPC onto date_str, with the window start as a
// Pacific DATE rather than a timestamp. So nothing in this file had to change
// for that ruling — it was already on the right column, which is why date_str
// was the basis chosen. The only thing that moved is this note.

/** Days to reach back from the reported day. Monday and Sunday reach Saturday. */
export function dailyWindowOffsetDays(isoDow) {
  if (isoDow === 1) return 2;   // Monday -> preceding Saturday
  if (isoDow === 7) return 1;   // Sunday -> preceding Saturday
  return 0;                      // Tue–Sat -> that day
}

/**
 * ISO weekday (1 = Monday … 7 = Sunday) for a 'YYYY-MM-DD' string.
 *
 * Parsed as UTC deliberately: `new Date('2026-10-12T00:00:00Z')` and
 * getUTCDay() give the weekday of the DATE AS WRITTEN. getDay() would shift it
 * a day for any viewer west of UTC — which is every viewer this app has — and
 * Monday's window would silently lose Saturday.
 */
export function isoDowFromDateStr(dateStr) {
  const js = new Date(`${dateStr}T00:00:00Z`).getUTCDay();   // 0 = Sunday
  return js === 0 ? 7 : js;
}

/** 'YYYY-MM-DD' shifted back by n whole days. Pure calendar arithmetic. */
export function shiftDateStr(dateStr, days) {
  const d = new Date(`${dateStr}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

/**
 * Today's date in Pacific, as 'YYYY-MM-DD'.
 *
 * The module-level todayStr in App.js uses new Date() and the browser's local
 * calendar. That agrees for a Pacific viewer, which is everyone today, but it
 * is not what the rule says and it is wrong from anywhere else.
 */
export function pacificTodayStr(now = new Date()) {
  const p = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles',
      year: 'numeric', month: '2-digit', day: '2-digit',
    }).formatToParts(now).map((x) => [x.type, x.value])
  );
  return `${p.year}-${p.month}-${p.day}`;
}

/** The first date string inside the window that ends on `todayStr`. */
export function dailyWindowStartStr(todayStr) {
  return shiftDateStr(todayStr, dailyWindowOffsetDays(isoDowFromDateStr(todayStr)));
}

/**
 * Is `dateStr` inside the daily window ending on `todayStr`?
 *
 * BOTH bounds are checked, and the upper one is not redundant: the testDate
 * override sets the effective day into the past, and a start-only comparison
 * would then sweep in every later entry — turning a diagnostic into a wrong
 * number on the same screen. ISO date strings compare correctly as strings.
 */
export function inDailyWindow(dateStr, todayStr) {
  if (!dateStr || !todayStr) return false;
  return dateStr >= dailyWindowStartStr(todayStr) && dateStr <= todayStr;
}
